// Global 401 / 402 / 403 handling for every plain axios call in the app.
// Interceptors are attached to the global `axios` (import axios from "axios"),
// the default instance in utils/axios and axiosPrivate, so existing
// axios.get/post/put/patch/delete calls get this handling without changing
// their payloads. useAxiosPrivate just returns axiosPrivate.
import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Swal from "sweetalert2";
import axiosService, { axiosPrivate } from "./axios";
import useRefreshToken from "../hooks/useRefreshToken";
import useAuth from "../hooks/useAuth";

// Calls made by login / logout / refresh itself. A 401 here must never
// trigger another refresh (it would loop or deadlock).
const SKIP_URLS = [
  "auth/token/refresh",
  "auth/agent",
  "auth/login",
  "auth/logout",
  "contact_permission",
];

const live = {}; // latest refresh / navigate / setAuth from the component
let refreshPromise = null;
let sessionEnded = false;
// Token from the last successful refresh. live.getToken() reads a ref that only
// updates on the next render, so right after a refresh it can still be the old one.
let lastRefreshedToken = null;

// Calls that must not get the stored token attached. auth/agent is not here:
// it skips refresh, but callers like useAgentHeader rely on the token being added.
const NO_TOKEN_URLS = SKIP_URLS.filter((u) => u !== "auth/agent");

const shouldSkip = (config) =>
  !config || SKIP_URLS.some((u) => config.url?.includes(u));

const shouldSkipToken = (config) =>
  !config || NO_TOKEN_URLS.some((u) => config.url?.includes(u));

// Only send our token to our own backend, never to third-party URLs.
const isOwnApi = (url = "") =>
  !/^https?:\/\//i.test(url) ||
  (!!process.env.REACT_APP_API_URL && url.startsWith(process.env.REACT_APP_API_URL));

// Adds the current access token when the call didn't set Authorization itself.
const requestHandler = (config) => {
  const token = live.getToken?.();
  if (token && !config.headers["Authorization"] && !shouldSkipToken(config) && isOwnApi(config.url)) {
    config.headers["Authorization"] = `Bearer ${token}`;
  }
  return config;
};

const onLoginPage = () => window.location.pathname.toLowerCase().startsWith("/login");

// Tell the backend the agent / admin is logged out (status, session cleanup).
// Best effort: when the refresh cookie itself has expired there is no valid
// token left, so the backend will answer 401 unless logout accepts an expired
// access token. The user is sent to /login either way. auth/logout is in
// SKIP_URLS, so its 401 can't loop back here.
const LOGOUT_URL = "/contactcentre/auth/logout/";
const backendLogout = (token) =>
  axiosService
    .post(
      LOGOUT_URL,
      {},
      {
        headers: {
          "Content-Type": "application/json",
          ...(token && { Authorization: `Bearer ${token}` }),
        },
        withCredentials: true,
      }
    )
    .catch((err) => console.error("Logout on session expiry failed:", err));

const endSession = () => {
  if (sessionEnded) return;
  sessionEnded = true;
  refreshPromise = null;
  backendLogout(lastRefreshedToken || live.getToken?.());
  lastRefreshedToken = null;
  live.setAuth?.((prev) => ({ ...prev, accessToken: null }));
  localStorage.setItem("IsLoggedIn", "false");
  localStorage.removeItem("onlineStatus");
  live.navigate?.("/login", { replace: true });
  Swal.fire({
    title: "Session Expired",
    text: "Your session has expired. Please log in again.",
    icon: "warning",
    confirmButtonText: "OK",
  });
};

// Session is over and we're redirecting to /login: leave the caller's promise
// pending instead of rejecting, so calls without .catch / try-catch don't show
// "Uncaught runtime errors: 401". The page unmounts right after.
const halt = () => new Promise(() => {});

const handle401 = async (instance, error) => {
  const original = error.config;
  // Checked before anything redirects: endSession / useRefreshToken navigate to
  // /login, after which the URL would always say "login page".
  const startedOnLogin = !sessionEnded && onLoginPage();

  const endAndHalt = () => {
    // Already on the login page with no session: nothing redirects, so let the caller see the error.
    if (startedOnLogin) return Promise.reject(error);
    endSession();
    return halt();
  };

  // Already logged out, or retried with a fresh token and still 401 -> user is no longer valid.
  if (sessionEnded || original._retry) return endAndHalt();
  original._retry = true;

  try {
    // Many requests can 401 together; refresh only once and share the result.
    refreshPromise ??= live.refresh().finally(() => (refreshPromise = null));
    const token = await refreshPromise;
    if (!token) return endAndHalt();
    lastRefreshedToken = token;
    original.headers["Authorization"] = `Bearer ${token}`;
    return instance(original);
  } catch (refreshError) {
    // Refresh cookie expired / invalid -> log out. Network or 5xx -> just fail this call.
    if ([400, 401, 403].includes(refreshError?.response?.status)) return endAndHalt();
    return Promise.reject(error);
  }
};

const responseErrorHandler = (instance) => async (error) => {
  const status = error?.response?.status;
  const config = error?.config;

  if (!live.refresh || shouldSkip(config)) return Promise.reject(error);

  if (status === 401) return handle401(instance, error);

  if (status === 402) {
    Swal.fire({
      title: "Payment Required",
      text: error.response?.data?.detail || "Your subscription or balance needs attention.",
      icon: "warning",
    });
  } else if (status === 403) {
    Swal.fire({
      title: "Access Denied",
      text: error.response?.data?.detail || "You do not have permission to perform this action.",
      icon: "warning",
    });
  }
  return Promise.reject(error);
};

// Registered once at import time (not per render) so they are never duplicated
// and are already active before any component fires its first request.
// On hot reload this module runs again; eject the previous copies first so the
// global axios never ends up with stale or duplicated interceptors.
[axios, axiosService, axiosPrivate].forEach((instance) => {
  const prev = instance.__authInterceptorIds;
  if (prev) {
    instance.interceptors.request.eject(prev.request);
    instance.interceptors.response.eject(prev.response);
  }
  instance.__authInterceptorIds = {
    request: instance.interceptors.request.use(requestHandler),
    response: instance.interceptors.response.use(
      (response) => response,
      responseErrorHandler(instance)
    ),
  };
});

// Mount once inside <BrowserRouter> and <AuthProvider>. Renders nothing.
const AxiosInterceptor = () => {
  const { auth, setAuth } = useAuth();
  const refresh = useRefreshToken();
  const navigate = useNavigate();
  const refreshRef = useRef(refresh);
  refreshRef.current = refresh;
  const tokenRef = useRef(auth?.accessToken);
  tokenRef.current = auth?.accessToken;

  live.refresh = () => refreshRef.current();
  live.getToken = () => tokenRef.current;
  live.navigate = navigate;
  live.setAuth = setAuth;

  // A new login gives a new token: allow the session-expired flow again.
  useEffect(() => {
    if (auth?.accessToken) sessionEnded = false;
  }, [auth?.accessToken]);

  return null;
};

export default AxiosInterceptor;
